import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import SEO from '@/components/base/SEO';

export default function MovieDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const movie = movies.find((m) => m.slug === slug);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <SEO
          title="影片未找到 | 快看影视"
          description="抱歉，您要查看的影片不存在或已下架。快看影视为您提供海量高清免费电影资源。"
          canonicalUrl={`https://kuikanys.com/movie/${slug}`}
        />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 pt-20 pb-20">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-50 mb-2">影片未找到</h1>
              <p className="text-foreground-400 mb-6">抱歉，您要查看的影片不存在或已下架。</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-all duration-200 cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: movie.category, url: `/category/${movie.categorySlug}` },
    { name: movie.title, url: `/movie/${movie.slug}` },
  ];

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 4);

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((actor) => ({
      '@type': 'Person',
      name: actor,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      worstRating: '0',
    },
  };

  return (
    <>
      <SEO
        title={`${movie.title} - 高清免费在线观看 | 快看影视`}
        description={`快看影视提供《${movie.title}》高清免费在线观看。导演${movie.director}，主演${movie.actors.slice(0, 3).join('、')}。${movie.description.substring(0, 80)}...播放流畅不卡顿。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.category},免费电影,高清电影,快看影视`}
        canonicalUrl={`https://kuikanys.com/movie/${movie.slug}`}
        ogType="video.movie"
        ogImage={movie.poster}
      />
      <script type="application/ld+json">{JSON.stringify(movieSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article className="w-full px-4 md:px-6 pt-8 md:pt-10 pb-12">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 w-full md:w-64">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      title={`${movie.title} - 高清电影在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-3">
                    {movie.title}
                  </h1>

                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <span className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium">
                      ★ {movie.rating}
                    </span>
                    <span className="text-sm text-foreground-400">{movie.year}</span>
                    <span className="text-sm text-foreground-400">{movie.duration}</span>
                    <Link
                      to={`/category/${movie.categorySlug}`}
                      className="text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer"
                    >
                      {movie.category}
                    </Link>
                  </div>

                  <div className="flex gap-2 flex-wrap mb-5">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-500/10 text-secondary-400 text-xs font-medium border border-secondary-500/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 w-16 flex-shrink-0">导演</span>
                      <span className="text-sm text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 w-16 flex-shrink-0">主演</span>
                      <span className="text-sm text-foreground-200">{movie.actors.join(' / ')}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h2 className="text-sm font-semibold text-foreground-200 mb-2">剧情简介</h2>
                    <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
                  </div>

                  <button className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line text-lg"></i>
                    立即播放
                  </button>
                </div>
              </div>

              {relatedMovies.length > 0 && (
                <div className="mt-14 pt-10 border-t border-background-200/70">
                  <h2 className="text-lg font-bold text-foreground-100 font-heading mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} {...m} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}